import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { OrderItem } from '../../../models/order/orders';
import { Cart } from '../../../models/cart/cart';
import { User } from '../../../models/user/user';
import { Address } from '../../../models/user/address';
import { PaymentMethod } from '../../../models/payment/payment';
import { getMerchantName, getProductImageUrl } from '../../../models/product/product';
import {
  SHIPPING_CITIES,
  calculateShippingFee,
  getLocalizedCityName,
  getShippingCityById
} from '../../../mock-data/shipping-cities';
import { StorageUtil } from '../../../utils/storage-util';
import { orderService, CreateOrderInput } from '../../../services/order-service';

const Checkout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = i18n.language === 'ar';

  // Get cart data from navigation state
  const cart = location.state?.cart as Cart;

  // State management
  const [currentStep, setCurrentStep] = useState<'shipping' | 'payment' | 'review'>('shipping');
  const [isGuest, setIsGuest] = useState(true);
  const [loading, setLoading] = useState(false);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);

  // Convert cart items to order items
  useEffect(() => {
    if (cart && cart.items.length > 0) {
      const items: OrderItem[] = cart.items.map(cartItem => ({
        id: cartItem.product.id,
        product: cartItem.product,
        quantity: cartItem.quantity,
      }));
      setOrderItems(items);
    } else {
      // If no cart data, redirect back to books page
      navigate(ROUTES.STORE);
    }
  }, [cart, navigate]);

  // Form data
  const [customerInfo, setCustomerInfo] = useState<User>({
    firstName: '',
    lastName: '',
    phone: '',
    isGuest: true
  });

  const [shippingAddress, setShippingAddress] = useState<Address>({
    street: '',
    cityId: null,
    city: '',
    state: '',
    country: 'Egypt',
    shippingFee: 0
  });

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.COD);

  // Validation states
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    phone?: string;
    street?: string;
  }>({});

  // Order submission error state
  const [orderError, setOrderError] = useState<string | null>(null);

  // Calculate totals with dynamic shipping
  const subtotal = orderItems.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  const shipping = calculateShippingFee(shippingAddress.cityId);
  const tax = 0; // No tax for now
  const total = subtotal + shipping + tax;

  // Update shipping address when city changes
  useEffect(() => {
    if (shippingAddress.cityId) {
      const selectedCity = getShippingCityById(shippingAddress.cityId);
      if (selectedCity) {
        setShippingAddress(prev => ({
          ...prev,
          city: getLocalizedCityName(selectedCity, i18n.language as 'en' | 'ar'),
          state: getLocalizedCityName(selectedCity, i18n.language as 'en' | 'ar'),
          shippingFee: selectedCity.shippingFee
        }));
      }
    }
  }, [shippingAddress.cityId, i18n.language]);

  // Validation functions
  const validateName = (name: string, fieldName: string): string | undefined => {
    if (!name || name.trim() === '') {
      return t(`checkout.error.${fieldName}-required`, `${fieldName} is required`);
    }
    if (name.trim() !== name.trim().replace(/[^a-zA-Z\u0600-\u06FF\s]/g, '')) {
      return t(`checkout.error.${fieldName}-letters-only`, `${fieldName} should contain letters only`);
    }
    if (name.trim().length === 0) {
      return t(`checkout.error.${fieldName}-no-spaces`, `${fieldName} cannot be spaces only`);
    }
    return undefined;
  };

  const validatePhone = (phone: string): string | undefined => {
    if (!phone || phone.trim() === '') {
      return t('checkout.error.phone-required', 'Phone number is required');
    }
    const phoneRegex = /^\+?[0-9\s-]+$/;
    if (!phoneRegex.test(phone)) {
      return t('checkout.error.phone-invalid', 'Phone number should contain numbers only and may start with +');
    }
    return undefined;
  };

  const validateStreet = (street: string): string | undefined => {
    if (!street || street.trim() === '') {
      return t('checkout.error.street-required', 'Street address is required');
    }
    if (street.trim().length === 0) {
      return t('checkout.error.street-no-spaces', 'Street address cannot be spaces only');
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    
    const firstNameError = validateName(customerInfo.firstName, 'first-name');
    const lastNameError = validateName(customerInfo.lastName, 'last-name');
    const phoneError = validatePhone(customerInfo.phone);
    const streetError = validateStreet(shippingAddress.street);
    
    if (firstNameError) newErrors.firstName = firstNameError;
    if (lastNameError) newErrors.lastName = lastNameError;
    if (phoneError) newErrors.phone = phoneError;
    if (streetError) newErrors.street = streetError;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submissions
  const handleCustomerInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setCurrentStep('payment');
    }
  };

  const handlePaymentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep('review');
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    setOrderError(null);

    // Validate shipping city is selected
    if (!shippingAddress.cityId) {
      setOrderError(t('checkout.error.select-governorate', 'Please select a governorate'));
      setLoading(false);
      return;
    }

    // Prepare order input for the service
    const orderInput: CreateOrderInput = {
      items: orderItems,
      shippingCityId: shippingAddress.cityId,
      address: shippingAddress,
      customerInfo,
      paymentMethod,
      subtotal,
      shipping,
      tax,
      total,
    };

    // Call the order service
    const result = await orderService.createOrder(orderInput);

    setLoading(false);

    if (result.success) {
      // Clear the cart after successful order placement
      const emptyCart = { items: [], total: 0, itemCount: 0 };
      StorageUtil.setConfig(StorageUtil.KEYS.CART_ITEMS, emptyCart);

      // Navigate to success page
      navigate(ROUTES.ORDER_SUCCESS, { state: { order: result.order } });
    } else {
      // Show error message
      const errorMessage = result.errorCode
        ? orderService.getErrorMessage(result.errorCode, result.error)
        : result.error;
      setOrderError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
            <button
              onClick={() => navigate(ROUTES.STORE)}
              className="text-gray-600 hover:text-black transition-colors"
            >
              <svg className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('checkout.title', 'Checkout')}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Forms */}
          <div className="lg:col-span-2 space-y-6">
            {/* Progress Steps */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                {[
                  { key: 'shipping', label: t('checkout.steps.shipping', 'Shipping Info'), step: 1 },
                  { key: 'payment', label: t('checkout.steps.payment', 'Payment'), step: 2 },
                  { key: 'review', label: t('checkout.steps.review', 'Review Order'), step: 3 }
                ].map((stepInfo, index) => (
                  <div key={stepInfo.key} className={`flex items-center ${index < 2 ? 'flex-1' : ''}`}>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      currentStep === stepInfo.key
                        ? 'bg-black text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {stepInfo.step}
                    </div>
                    <span className={`ml-2 text-sm font-medium ${
                      currentStep === stepInfo.key ? 'text-black' : 'text-gray-600'
                    }`}>
                      {stepInfo.label}
                    </span>
                    {index < 2 && (
                      <div className={`flex-1 h-0.5 mx-4 ${
                        ['payment', 'review'].includes(currentStep) && index === 0
                          ? 'bg-black'
                          : currentStep === 'review' && index === 1
                          ? 'bg-black'
                          : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Step Content */}
            {currentStep === 'shipping' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  {t('checkout.shipping-info.title', 'Shipping Information')}
                </h2>

                {/* Guest vs Login Toggle */}
                <div className="mb-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <button
                      onClick={() => setIsGuest(true)}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        isGuest 
                          ? 'bg-black text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t('checkout.guest-checkout', 'Guest Checkout')}
                    </button>
                    {/* <button
                      onClick={() => setIsGuest(false)}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        !isGuest 
                          ? 'bg-black text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t('checkout.login', 'Login')}
                    </button> */}
                  </div>

                  {!isGuest && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-800 text-sm">
                        {t('checkout.login-message', 'Login functionality will be implemented soon. For now, please use guest checkout.')}
                      </p>
                    </div>
                  )}
                </div>

                <form onSubmit={handleCustomerInfoSubmit} className="space-y-6">
                  {/* Customer Information */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {t('checkout.contact-info', 'Contact Information')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('checkout.first-name', 'First Name')} *
                        </label>
                        <input
                          type="text"
                          required
                          value={customerInfo.firstName}
                          onChange={(e) => {
                            setCustomerInfo({...customerInfo, firstName: e.target.value});
                            if (errors.firstName) {
                              const error = validateName(e.target.value, 'first-name');
                              setErrors(prev => ({...prev, firstName: error}));
                            }
                          }}
                          onBlur={(e) => {
                            const error = validateName(e.target.value, 'first-name');
                            setErrors(prev => ({...prev, firstName: error}));
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                            errors.firstName 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-black'
                          }`}
                        />
                        {errors.firstName && (
                          <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('checkout.last-name', 'Last Name')} *
                        </label>
                        <input
                          type="text"
                          required
                          value={customerInfo.lastName}
                          onChange={(e) => {
                            setCustomerInfo({...customerInfo, lastName: e.target.value});
                            if (errors.lastName) {
                              const error = validateName(e.target.value, 'last-name');
                              setErrors(prev => ({...prev, lastName: error}));
                            }
                          }}
                          onBlur={(e) => {
                            const error = validateName(e.target.value, 'last-name');
                            setErrors(prev => ({...prev, lastName: error}));
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                            errors.lastName 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-black'
                          }`}
                        />
                        {errors.lastName && (
                          <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('checkout.phone', 'Phone Number')} *
                        </label>
                        <input
                          type="tel"
                          required
                          value={customerInfo.phone}
                          onChange={(e) => {
                            setCustomerInfo({...customerInfo, phone: e.target.value});
                            if (errors.phone) {
                              const error = validatePhone(e.target.value);
                              setErrors(prev => ({...prev, phone: error}));
                            }
                          }}
                          onBlur={(e) => {
                            const error = validatePhone(e.target.value);
                            setErrors(prev => ({...prev, phone: error}));
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                            errors.phone 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-black'
                          }`}
                          placeholder="+20 123 456 7890"
                        />
                        {errors.phone && (
                          <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {t('checkout.shipping-address', 'Shipping Address')}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('checkout.street-address', 'Street Address')} *
                        </label>
                        <input
                          type="text"
                          required
                          value={shippingAddress.street}
                          onChange={(e) => {
                            setShippingAddress({...shippingAddress, street: e.target.value});
                            if (errors.street) {
                              const error = validateStreet(e.target.value);
                              setErrors(prev => ({...prev, street: error}));
                            }
                          }}
                          onBlur={(e) => {
                            const error = validateStreet(e.target.value);
                            setErrors(prev => ({...prev, street: error}));
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                            errors.street 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-300 focus:ring-black'
                          }`}
                        />
                        {errors.street && (
                          <p className="text-red-500 text-xs mt-1">{errors.street}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('checkout.governorate', 'Governorate')} *
                          </label>
                          <select
                            required
                            value={shippingAddress.cityId || ''}
                            onChange={(e) => {
                              const cityId = e.target.value ? parseInt(e.target.value) : null;
                              setShippingAddress({...shippingAddress, cityId});
                            }}
                            dir={isRTL ? 'rtl' : 'ltr'}
                            className={`w-full py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white ${
                              isRTL 
                                ? 'pr-4 pl-8 text-right' 
                                : 'pl-4 pr-8 text-left'
                            }`}
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: isRTL ? 'left 0.75rem center' : 'right 0.75rem center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '1.5em 1.5em'
                            }}
                          >
                            <option value="">
                              {t('checkout.select-governorate', 'Select Governorate')}
                            </option>
                            {SHIPPING_CITIES
                              .sort((a, b) => a.shippingFee - b.shippingFee) // Sort by shipping fee (closest first)
                              .map((city) => (
                              <option key={city.id} value={city.id}>
                                {getLocalizedCityName(city, i18n.language as 'en' | 'ar')} - {city.shippingFee} EGP
                              </option>
                            ))}
                          </select>
                          {shippingAddress.cityId && (
                            <p className="text-xs text-gray-600 mt-1">
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="bg-black text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                    >
                      {t('checkout.continue-payment', 'Continue to Payment')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {currentStep === 'payment' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  {t('checkout.payment.title', 'Payment Method')}
                </h2>

                <form onSubmit={handlePaymentSubmit} className="space-y-6">
                  {/* Payment Methods */}
                  <div>
                    <div className="space-y-4">
                      <div className="border border-gray-300 rounded-lg p-4">
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name="payment"
                            value={PaymentMethod.COD}
                            checked={paymentMethod === PaymentMethod.COD}
                            onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                            className="w-4 h-4 text-black focus:ring-black border-gray-300"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {t('checkout.payment.cod', 'Cash on Delivery (COD)')}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {t('checkout.payment.cod-description', 'Pay when you receive your order')}
                                </div>
                              </div>
                            </div>
                          </div>
                        </label>
                      </div>

                      {/* Future payment methods - disabled for now */}
                      <div className="border border-gray-200 rounded-lg p-4 opacity-50">
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            disabled
                            className="w-4 h-4 text-gray-400 border-gray-300"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                              <div>
                                <div className="font-medium text-gray-500">
                                  {t('checkout.payment.credit-card', 'Credit/Debit Card')}
                                </div>
                                <div className="text-sm text-gray-400">
                                  {t('checkout.payment.coming-soon', 'Coming Soon')}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={() => setCurrentStep('shipping')}
                      className="bg-gray-100 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                    >
                      {t('checkout.back', 'Back')}
                    </button>
                    <button
                      type="submit"
                      className="bg-black text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                    >
                      {t('checkout.review-order', 'Review Order')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {currentStep === 'review' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  {t('checkout.review.title', 'Review Your Order')}
                </h2>

                <div className="space-y-6">
                  {/* Order Items */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-4">
                      {t('checkout.review.items', 'Order Items')}
                    </h3>
                    <div className="space-y-4">
                      {orderItems.map((item) => (
                        <div key={`${item.id}-${item.product.type}`} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                          <img
                            src={getProductImageUrl(item.product)}
                            alt={item.product.name}
                            className="w-16 h-20 object-cover rounded"
                          />
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{item.product.name}</h4>
                            <p className="text-sm text-gray-600">{t('products.books.by-author', 'by')} {getMerchantName(item.product)}</p>
                            <p className="text-sm text-gray-600">
                              {t('checkout.quantity', 'Qty')}: {item.quantity}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">
                              {(item.product.price * item.quantity).toFixed(0)} EGP
                            </p>
                            {item.quantity > 1 && (
                              <p className="text-sm text-gray-600">
                                {item.product.price.toFixed(0)} EGP × {item.quantity}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Shipping Info */}
                    <div>
                      <h3 className="font-medium text-gray-900 mb-3">
                        {t('checkout.review.shipping-info', 'Shipping Information')}
                      </h3>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>{customerInfo.firstName} {customerInfo.lastName}</p>
                        <p>{customerInfo.phone}</p>
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p>{shippingAddress.street}</p>
                          <p>{shippingAddress.city}, {shippingAddress.state}</p>
                          <p>{shippingAddress.country}</p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div>
                      <h3 className="font-medium text-gray-900 mb-3">
                        {t('checkout.review.payment-method', 'Payment Method')}
                      </h3>
                      <div className="flex items-center space-x-3">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="text-gray-900">
                          {t('checkout.payment.cod', 'Cash on Delivery (COD)')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Order Error Display */}
                  {orderError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start space-x-3">
                        <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-red-800 font-medium">
                            {t('checkout.error.order-failed', 'Order could not be placed')}
                          </p>
                          <p className="text-red-700 text-sm mt-1">{orderError}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={() => setCurrentStep('payment')}
                      className="bg-gray-100 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                    >
                      {t('checkout.back', 'Back')}
                    </button>
                    <button
                      onClick={handlePlaceOrder}
                      disabled={loading}
                      className="bg-black text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {loading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      )}
                      <span>
                        {loading
                          ? t('checkout.placing-order', 'Placing Order...')
                          : t('checkout.place-order', 'Place Order')
                        }
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('checkout.order-summary', 'Order Summary')}
              </h3>

              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('checkout.subtotal', 'Subtotal')}:</span>
                  <span className="font-medium">{subtotal.toFixed(0)} {t('currency.egp', 'EGP')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('checkout.shipping', 'Shipping')}:</span>
                  <span className="font-medium text-gray-600">
                    {shippingAddress.cityId 
                      ? `${shipping.toFixed(0)} ${t('currency.egp', 'EGP')}` 
                      : t('checkout.shipping-tbd', 'To be determined')
                    }
                  </span>
                </div>
                {shippingAddress.cityId && (
                  <div className="text-xs text-gray-500">
                    {t('checkout.delivery-to', 'Delivery to')}: {shippingAddress.city}
                  </div>
                )}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-lg font-bold">
                    <span>{t('checkout.total', 'Total')}:</span>
                    <span>{total.toFixed(0)} {t('currency.egp', 'EGP')}</span>
                  </div>
                </div>
              </div>

              {shippingAddress.cityId && shipping > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-blue-800 text-sm">
                    {t('checkout.shipping-notice', 'Shipping to {{city}}: {{fee}} EGP', {
                      city: shippingAddress.city,
                      fee: shipping
                    })}
                  </p>
                </div>
              )}

              <div className="text-xs text-gray-500 space-y-1">
                <p>
                  {shippingAddress.cityId 
                    ? t('checkout.estimated-delivery', 'Estimated delivery')
                    : t('checkout.estimated-delivery-range', 'Estimated delivery: 3-7 business days')
                  }
                </p>
                <p>{t('checkout.secure-checkout', 'Secure checkout guaranteed')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;